import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import {
  RegisterReviewSection,
  RegisterReviewSectionTitle,
} from "../../../Account/Register/Register.style";
import { COLORS } from "../../../../shared/theme/colorConfig";
import {
  DocumentSection,
  DocumentSpec,
  getRequiredSections,
} from "../documentRequirements";
import {
  useGetApplicationDocumentPresignedUrlMutation,
  useConfirmApplicationDocumentUploadMutation,
  useDeleteApplicationDocumentUploadMutation,
  useCheckApplicationDocumentLegibilityMutation,
} from "../../../../store/services/privateApi";
import type { ApplicationData } from "../applicationState";

// Upload-time quality/legibility nudge verdict. "checking" is a client-only
// pending state; the rest come from the server (affordable flow only). Absent /
// "pass" / "skipped" render no nudge — only warn/fail/unsupported surface one.
export type LegibilityStatus =
  | "checking"
  | "pass"
  | "warn"
  | "fail"
  | "unsupported"
  | "skipped";

export interface UploadedFile {
  s3Key: string;
  fileName: string;
  fileSize: number;
  uploadedAt: string;
  // document_upload row id returned by the confirm endpoint. Lets us soft-delete
  // the recorded file (and emit document:deleted) when the applicant removes it.
  // Optional: files hydrated from older saves predate the confirm wiring.
  fileId?: string;
  // Non-blocking legibility nudge; persisted so the hint survives a reload.
  legibility?: { status: LegibilityStatus; reasons?: string[] };
}
export type DocumentsState = Record<string, UploadedFile[]>;

export interface UploadCenterForm {
  reviewed: true;
  documents: DocumentsState;
  // Set from the "I'd like help with my documents" checkbox, which only shows
  // when the property has made uploads optional. Lets staff know an applicant
  // who submitted without docs wants assistance finishing this step.
  needsDocumentHelp?: boolean;
}

interface UploadCenterProps {
  applicationId: string;
  applicationData: ApplicationData;
  onSubmit: (data: UploadCenterForm) => void | Promise<void>;
  // Fired after every successful upload/delete so the slot↔file mapping is
  // persisted incrementally. Without this the applicant must finish the
  // step in one session or re-upload everything on revisit (the files
  // themselves are in S3, but applicationData wouldn't know about them).
  onPartialSave?: (data: { documents: DocumentsState }) => void | Promise<void>;
  onBack: () => void;
  isSaving?: boolean;
  // Per-property flag from the income-limits response. When true, the step is
  // non-blocking: the applicant can advance/submit without uploading required
  // docs, and a "need help" checkbox is offered. Default (false) keeps the gate.
  documentUploadOptional?: boolean;
}

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Clear any client-only "checking" state left over from a previous session so a
// reload never shows a permanent spinner (the in-flight request didn't survive
// the tab close). Server statuses (warn/fail/unsupported/pass) are kept as-is.
const sanitizeHydratedDocs = (docs: DocumentsState): DocumentsState => {
  const out: DocumentsState = {};
  for (const [key, files] of Object.entries(docs)) {
    out[key] = files.map((f) =>
      f.legibility?.status === "checking" ? { ...f, legibility: undefined } : f
    );
  }
  return out;
};

interface DocumentRowProps {
  spec: DocumentSpec;
  uploaded: UploadedFile[];
  onUpload: (docKey: string, file: File) => Promise<void>;
  onDelete: (docKey: string, key: string) => Promise<void>;
  isBusy: boolean;
}

export const DocumentRow: React.FC<DocumentRowProps> = ({
  spec,
  uploaded,
  onUpload,
  onDelete,
  isBusy,
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [localBusy, setLocalBusy] = useState(false);

  const count = uploaded.length;
  // Required slots accept 1–N files; optional slots accept 0–N. The upper
  // ceiling defaults to 6 so the resident isn't flooded with file slots
  // but can still upload several months of statements / pages as needed.
  // Override per-spec via `maxFiles` when a hard cap is required (e.g.
  // single ID photo).
  const minRequired = spec.optional ? 0 : spec.count;
  const uploadCap = spec.maxFiles ?? 6;
  const allFilled = count >= minRequired;
  const allowsMultiple = uploadCap > 1;

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setLocalBusy(true);
    try {
      // Upload up to (uploadCap - count) files
      const remaining = uploadCap - count;
      const toUpload = Array.from(files).slice(0, remaining);
      for (const file of toUpload) {
        await onUpload(spec.key, file);
      }
    } finally {
      setLocalBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const i18nKey = spec.baseKey || spec.key;
  const subjectLabel = spec.subject || "";

  return (
    <Box
      sx={{
        border: `1px solid ${COLORS.gray[200]}`,
        borderRadius: 2,
        p: 2.5,
        mb: 2,
        backgroundColor: "white",
      }}
    >
      {/* Title + quantity badge */}
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5, mb: 0.5 }}>
        <Typography variant="body1" sx={{ fontWeight: 600 }}>
          {t(`affordable.application.uploadCenter.documents.${i18nKey}.title`)}
          {subjectLabel && (
            <Box
              component="span"
              sx={{ color: COLORS.gray[600], fontWeight: 400, ml: 0.5 }}
            >
              — {subjectLabel}
            </Box>
          )}
        </Typography>
        <Box
          component="span"
          sx={{
            fontSize: 12,
            color: COLORS.gray[600],
            backgroundColor: COLORS.gray[100],
            px: 1,
            py: 0.25,
            borderRadius: 1,
          }}
        >
          {uploadCap === 1
            ? t("affordable.application.uploadCenter.oneDocument")
            : t("affordable.application.uploadCenter.upToNDocuments", {
                count: uploadCap,
              })}
        </Box>
        {spec.optional && (
          <Box
            component="span"
            sx={{
              fontSize: 11,
              fontWeight: 600,
              color: COLORS.gray[700],
              border: `1px solid ${COLORS.gray[300]}`,
              px: 1,
              py: 0.25,
              borderRadius: 1,
              textTransform: "uppercase",
              letterSpacing: 0.5,
            }}
          >
            {t("affordable.application.uploadCenter.optional")}
          </Box>
        )}
        {allFilled && (
          <CheckCircleOutlineIcon
            sx={{ color: COLORS.green[500], fontSize: 20, ml: "auto" }}
          />
        )}
      </Box>

      {/* Description */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {t(
          `affordable.application.uploadCenter.documents.${i18nKey}.description`
        )}
      </Typography>

      {/* Template download — third-party verification forms (VOE, etc.) the
          applicant hands to an employer / agency / landlord to fill out. */}
      {spec.hasTemplate && spec.templateUrl && (
        <Button
          variant="text"
          size="small"
          component="a"
          href={spec.templateUrl}
          target="_blank"
          rel="noopener noreferrer"
          startIcon={<FileDownloadOutlinedIcon />}
          sx={{ mb: 1.5, textTransform: "none", p: 0, minWidth: 0 }}
        >
          {t("affordable.application.uploadCenter.downloadTemplate")}
        </Button>
      )}

      {/* Upload zone — shown until the upload cap is reached. Gating on the
          cap (not minRequired) lets a resident add beyond the minimum, e.g.
          several months of statements or a multi-stub paystub set, which the
          "x / {uploadCap}" counter already advertises. */}
      {count < uploadCap && (
        <>
          <Box
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragOver(true);
            }}
            onDragLeave={() => setIsDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setIsDragOver(false);
              handleFiles(e.dataTransfer.files);
            }}
            onClick={() => fileInputRef.current?.click()}
            sx={{
              border: `2px dashed ${
                isDragOver ? COLORS.darkBlue[500] : COLORS.gray[300]
              }`,
              backgroundColor: isDragOver ? COLORS.blue[50] : COLORS.gray[50],
              borderRadius: 2,
              p: 3,
              textAlign: "center",
              cursor: "pointer",
              transition: "all 0.2s ease",
            }}
          >
            <CloudUploadOutlinedIcon
              sx={{ color: COLORS.gray[500], fontSize: 32, mb: 0.5 }}
            />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {t("affordable.application.uploadCenter.dragDrop")}{" "}
              <Box
                component="span"
                sx={{
                  color: COLORS.darkBlue[500],
                  textDecoration: "underline",
                }}
              >
                {t("affordable.application.uploadCenter.browse")}
              </Box>
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 0.25 }}
            >
              {t("affordable.application.uploadCenter.acceptHint", {
                maxMb: spec.maxSizeMb,
              })}
            </Typography>
            <input
              ref={fileInputRef}
              type="file"
              accept={spec.accept}
              hidden
              multiple={allowsMultiple}
              onChange={(e) => handleFiles(e.target.files)}
              data-testid={`file-input-${spec.key}`}
            />
          </Box>

          {(localBusy || isBusy) && (
            <LinearProgress sx={{ mt: 1, borderRadius: 1 }} />
          )}
        </>
      )}

      {/* Uploaded files list */}
      {uploaded.length > 0 && (
        <Box sx={{ mt: 1.5 }}>
          {uploaded.map((file) => {
            const legStatus = file.legibility?.status;
            const isChecking = legStatus === "checking";
            const showNudge =
              legStatus === "warn" ||
              legStatus === "fail" ||
              legStatus === "unsupported";
            return (
              <Box key={file.s3Key} sx={{ mb: 0.5 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    p: 1,
                    border: `1px solid ${COLORS.gray[200]}`,
                    borderRadius: 1,
                    backgroundColor: COLORS.gray[50],
                  }}
                >
                  <DescriptionOutlinedIcon
                    sx={{ color: COLORS.darkBlue[500], fontSize: 20 }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {file.fileName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatFileSize(file.fileSize)}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => onDelete(spec.key, file.s3Key)}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>

                {/* Non-blocking upload-time quality nudge (affordable flow). */}
                {isChecking && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      mt: 0.5,
                      ml: 0.5,
                    }}
                  >
                    <CircularProgress size={12} />
                    <Typography variant="caption" color="text.secondary">
                      {t(
                        "affordable.application.uploadCenter.legibility.checking"
                      )}
                    </Typography>
                  </Box>
                )}
                {showNudge && (
                  <Alert
                    severity="warning"
                    icon={<InfoOutlinedIcon fontSize="inherit" />}
                    sx={{ mt: 0.5, py: 0, alignItems: "center" }}
                  >
                    <Typography variant="caption">
                      {t(
                        `affordable.application.uploadCenter.legibility.${legStatus}`
                      )}
                    </Typography>
                  </Alert>
                )}
              </Box>
            );
          })}
        </Box>
      )}

      {/* Counter */}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mt: 1, textAlign: "right" }}
      >
        {count} / {uploadCap}
      </Typography>
    </Box>
  );
};

export const UploadCenter: React.FC<UploadCenterProps> = ({
  applicationId,
  applicationData,
  onSubmit,
  onPartialSave,
  onBack,
  isSaving = false,
  documentUploadOptional = false,
}) => {
  const { t } = useTranslation();

  const [getPresignedUrl] = useGetApplicationDocumentPresignedUrlMutation();
  const [confirmUpload] = useConfirmApplicationDocumentUploadMutation();
  const [deleteUpload] = useDeleteApplicationDocumentUploadMutation();
  const [checkLegibility] = useCheckApplicationDocumentLegibilityMutation();

  // Hydrate from applicationData.uploadCenter.documents (saved on previous visits).
  // A persisted "checking" would otherwise show a spinner forever after reload
  // (the in-flight request didn't survive the tab close), so clear it on hydrate.
  const savedDocs = sanitizeHydratedDocs(
    (applicationData.uploadCenter as { documents?: DocumentsState } | undefined)
      ?.documents || {}
  );
  const [documents, setDocuments] = useState<DocumentsState>(savedDocs);
  const [busyDocKey, setBusyDocKey] = useState<string | null>(null);
  const [needsDocumentHelp, setNeedsDocumentHelp] = useState<boolean>(
    Boolean(
      (
        applicationData.uploadCenter as
          | { needsDocumentHelp?: boolean }
          | undefined
      )?.needsDocumentHelp
    )
  );

  // Persist documents → applicationData on every change after first paint so
  // an applicant who closes the tab mid-upload can resume on revisit.
  // onPartialSave is held in a ref and intentionally kept OUT of the effect
  // deps: the parent passes a fresh inline callback every render, and the save
  // it performs updates applicationData → re-renders the parent → new callback.
  // Including it in the deps turned this into an unbounded PUT /application loop.
  // The effect must fire only when `documents` actually changes (upload/delete).
  const onPartialSaveRef = useRef(onPartialSave);
  useEffect(() => {
    onPartialSaveRef.current = onPartialSave;
  });
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    void onPartialSaveRef.current?.({ documents });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documents]);

  const sections: DocumentSection[] = getRequiredSections(applicationData);

  // Gate the step: every non-optional slot must hold at least its required
  // count of files before the applicant can advance. Without this the "Next"
  // button submitted with an empty `documents`, so required ID / income /
  // asset docs never reached ResMan — the backend
  // `uploadAffordableDocumentsToResman` only uploads what's captured here.
  const allRequiredUploaded = sections.every((section) =>
    section.documents.every(
      (spec) =>
        spec.optional || (documents[spec.key]?.length ?? 0) >= spec.count
    )
  );

  // On optional-upload properties the applicant may advance without every
  // required document, but only if they acknowledge it by checking "I'd like
  // help with my documents" — so staff know to follow up. Uploading all
  // required docs also satisfies the gate. Elsewhere the required-document
  // completeness gate stands (see commit 721cd54d).
  const uploadGateSatisfied = documentUploadOptional
    ? allRequiredUploaded || needsDocumentHelp
    : allRequiredUploaded;

  const handleUpload = async (docKey: string, file: File) => {
    setBusyDocKey(docKey);
    try {
      const presignRes = (await getPresignedUrl({
        applicationId,
        docKey,
        fileName: file.name,
        fileType: file.type,
      })) as { data?: { url: string; key: string } };
      if (!presignRes.data) return;
      const { url, key } = presignRes.data;
      const putRes = await fetch(url, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type },
      });
      if (!putRes.ok) throw new Error(`S3 upload failed: ${putRes.status}`);

      // Record the file as a document_upload row so it has attributed history and
      // emits a document:uploaded audit event. supersede:false — each requirement
      // here may hold several files (e.g. multiple pay stubs), so a new file must
      // not replace the previous one. Best-effort: a confirm failure must not lose
      // the upload from the form (it is still in S3 + applicationData).
      let fileId: string | undefined;
      try {
        const confirmRes = (await confirmUpload({
          applicationId,
          docKey,
          s3Key: key,
          fileName: file.name,
          mime: file.type || undefined,
          sizeBytes: file.size,
          supersede: false,
        })) as { data?: { id?: string } };
        fileId = confirmRes.data?.id;
      } catch {
        // swallow — the document still exists in S3 and the form state below
      }

      const newFile: UploadedFile = {
        s3Key: key,
        fileName: file.name,
        fileSize: file.size,
        uploadedAt: new Date().toISOString(),
        fileId,
        // Kick off the non-blocking quality nudge below; show a pending hint until
        // it returns. No fileId (confirm failed) → nothing to check.
        legibility: fileId ? { status: "checking" } : undefined,
      };
      setDocuments((prev) => ({
        ...prev,
        [docKey]: [...(prev[docKey] || []), newFile],
      }));

      // Fire-and-forget: the check must never delay the upload UI or block the
      // step (serial uploads would otherwise stall on each file). Patch this
      // file's verdict — keyed by s3Key — when it returns.
      if (fileId) void runLegibilityNudge(docKey, key, fileId);
    } finally {
      setBusyDocKey(null);
    }
  };

  // Patch one file's legibility verdict in place, matched by s3Key (never by
  // index — deletes/reorders would otherwise attach a verdict to the wrong file).
  const patchFileLegibility = (
    docKey: string,
    s3Key: string,
    legibility: UploadedFile["legibility"]
  ) => {
    setDocuments((prev) => ({
      ...prev,
      [docKey]: (prev[docKey] || []).map((f) =>
        f.s3Key === s3Key ? { ...f, legibility } : f
      ),
    }));
  };

  // Run the server-side quality/legibility check for a just-confirmed file and
  // surface only actionable verdicts (warn/fail/unsupported). pass/skipped/error
  // clear the pending hint — fail-open, the applicant is never blocked.
  const runLegibilityNudge = async (
    docKey: string,
    s3Key: string,
    fileId: string
  ) => {
    try {
      const res = (await checkLegibility({ applicationId, fileId })) as {
        data?: { status?: LegibilityStatus; reasons?: string[] };
      };
      const status = res.data?.status;
      if (status === "warn" || status === "fail" || status === "unsupported") {
        patchFileLegibility(docKey, s3Key, {
          status,
          reasons: res.data?.reasons,
        });
      } else {
        patchFileLegibility(docKey, s3Key, undefined);
      }
    } catch {
      patchFileLegibility(docKey, s3Key, undefined);
    }
  };

  const handleDelete = async (docKey: string, s3Key: string) => {
    // Soft-delete the recorded row (emits document:deleted) when we have its id.
    const target = (documents[docKey] || []).find((f) => f.s3Key === s3Key);
    if (target?.fileId) {
      try {
        await deleteUpload({ applicationId, fileId: target.fileId });
      } catch {
        // best-effort — still drop from the form state below
      }
    }
    setDocuments((prev) => {
      const next = { ...prev };
      const filtered = (next[docKey] || []).filter((f) => f.s3Key !== s3Key);
      if (filtered.length === 0) delete next[docKey];
      else next[docKey] = filtered;
      return next;
    });
  };

  // Group sections by sectionKey for the 3 big blocks
  const standardSections = sections.filter((s) => s.sectionKey === "standard");
  const incomeSections = sections.filter((s) => s.sectionKey === "income");
  const assetSections = sections.filter((s) => s.sectionKey === "asset");

  const renderSection = (section: DocumentSection) => (
    <RegisterReviewSection key={`${section.sectionKey}-${section.groupKey}`}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          mb: 0.5,
        }}
      >
        <RegisterReviewSectionTitle sx={{ mb: 0 }}>
          {t(
            `affordable.application.uploadCenter.groups.${section.groupKey}.title`
          )}
        </RegisterReviewSectionTitle>
        <Box
          component="span"
          sx={{
            fontSize: 11,
            fontWeight: 700,
            color: COLORS.red[500],
            backgroundColor: COLORS.red[50],
            px: 1,
            py: 0.25,
            borderRadius: 1,
            textTransform: "uppercase",
            letterSpacing: 0.5,
          }}
        >
          {t("affordable.application.uploadCenter.required")}
        </Box>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t(
          `affordable.application.uploadCenter.groups.${section.groupKey}.subtitle`
        )}
      </Typography>
      {section.documents.map((spec) => (
        <DocumentRow
          key={spec.key}
          spec={spec}
          uploaded={documents[spec.key] || []}
          onUpload={handleUpload}
          onDelete={handleDelete}
          isBusy={busyDocKey === spec.key}
        />
      ))}
    </RegisterReviewSection>
  );

  return (
    <Box>
      {/* Universal rules banner */}
      <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mb: 3 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
          {t("affordable.application.uploadCenter.universalRules.title")}
        </Typography>
        <Typography variant="body2">
          {t("affordable.application.uploadCenter.universalRules.freshness")}
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          {t("affordable.application.uploadCenter.universalRules.quality")}
        </Typography>
      </Alert>

      {/* Standard */}
      {standardSections.length > 0 && (
        <>
          <Typography
            variant="body2"
            sx={{
              color: COLORS.gray[600],
              fontWeight: 700,
              letterSpacing: 0.8,
              mb: 1.5,
              mt: 1,
            }}
          >
            {t("affordable.application.uploadCenter.sections.standard")}
          </Typography>
          {standardSections.map(renderSection)}
        </>
      )}

      {/* Income */}
      {incomeSections.length > 0 && (
        <>
          <Typography
            variant="body2"
            sx={{
              color: COLORS.gray[600],
              fontWeight: 700,
              letterSpacing: 0.8,
              mb: 1.5,
              mt: 2,
            }}
          >
            {t("affordable.application.uploadCenter.sections.income")}
          </Typography>
          {incomeSections.map(renderSection)}
        </>
      )}

      {/* Assets */}
      {assetSections.length > 0 && (
        <>
          <Typography
            variant="body2"
            sx={{
              color: COLORS.gray[600],
              fontWeight: 700,
              letterSpacing: 0.8,
              mb: 1.5,
              mt: 2,
            }}
          >
            {t("affordable.application.uploadCenter.sections.asset")}
          </Typography>
          {assetSections.map(renderSection)}
        </>
      )}

      {/* When uploads are required (the default) an incomplete step blocks the
          Next button below. When the property has made uploads optional, we only
          inform — nothing blocks — and offer a "need help" flag for staff. */}
      {!allRequiredUploaded &&
        (documentUploadOptional ? (
          <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mb: 2 }}>
            <Typography variant="body2">
              {t("affordable.application.uploadCenter.canSubmitLater")}
            </Typography>
          </Alert>
        ) : (
          <Alert severity="warning" icon={<InfoOutlinedIcon />} sx={{ mb: 2 }}>
            <Typography variant="body2">
              {t("affordable.application.uploadCenter.incompleteRequired")}
            </Typography>
          </Alert>
        ))}

      {documentUploadOptional && (
        <FormControlLabel
          sx={{ mb: 2 }}
          control={
            <Checkbox
              checked={needsDocumentHelp}
              onChange={(e) => setNeedsDocumentHelp(e.target.checked)}
            />
          }
          label={t("affordable.application.uploadCenter.needHelpCheckbox")}
        />
      )}

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mt: 3,
          mb: 4,
        }}
      >
        <Button
          variant="outlined"
          onClick={onBack}
          disabled={isSaving}
          startIcon={<ArrowBackIcon />}
        >
          {t("account.leaseApplication.previous")}
        </Button>
        <Button
          variant="contained"
          onClick={() =>
            onSubmit({ reviewed: true, documents, needsDocumentHelp })
          }
          disabled={isSaving || !uploadGateSatisfied}
          endIcon={
            isSaving ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <ArrowForwardIcon />
            )
          }
        >
          {t("account.leaseApplication.next")}
        </Button>
      </Box>
    </Box>
  );
};
